import { addDays, daysBetween, parseDay, todayStr } from "./date";
import { dinnerSettings } from "./dinnerNudge";
import { latestTests, placement, rangeFor } from "./fitnessTests";
import { moveById } from "./moves";
import { painRule, type JointRule } from "./readiness";
import { BP_FINISHER, SB_BALANCE, TEMPLATES, isSteadyKey, type Slot, type Template, type TemplateBlock } from "./templates";
import type {
  Block,
  BlockItem,
  Exercise,
  ExerciseId,
  JointKey,
  KayaState,
  MoveFlag,
  MoveTag,
  Programme,
  Session,
  SessionKey,
  SteadyKey,
  TrainingGoal,
  TrainingProfile,
} from "./types";

/**
 * Programmes are templates filled by the person's flags (ADR-052).
 *
 * Two programmes: Kaya (the reference's A/B/C week, unchanged) and Steady (the parents'). A Steady
 * session is one of five fixed templates (`templates.ts`) whose slots are filled here: a flag the
 * person carries swaps a slot's move for its `easier` id, or drops the slot when nothing clean is
 * left, and records why in the person's words. Deterministic — the same answers give the same
 * session — so the safety of a plan can be checked by reading this file and the tables it reads.
 *
 * Sources: docs/research/2026-10-05/move-library-older-adults.md §3–§4,
 * older-adults-guidelines.md §A, §5, §8, plans-by-age-sex-goal.md §2.4, §4.3, §5.1.
 */

/* ---------- the person's flags ---------- */

/**
 * What only this phone knows (`LocalMeta.trainingSafety`, plan §3.5), passed in by the pages the way
 * `useDeviceAnswers()` passes the food safety answer. `domain/` never reads `LocalMeta`.
 * - `doctor`: a yes on "Before you start" — the plan holds at walks and the gentle sessions
 * - `recent`: an operation, a fracture, a heart attack, a stroke or a fall that hurt in six months
 * - `footSore`: numb feet or an open foot sore — chair and bed only
 */
export interface TrainingDevice {
  doctor?: boolean;
  recent?: boolean;
  footSore?: boolean;
}

/**
 * A flag the filler reads. The move flags (`MoveFlag`) swap or soften a move that carries them;
 * the rest are facts about the person that change a slot without a move flag:
 * - `steady`: the Steady level (a failed test, a fall, unsteady) — a slot's `steady` move
 * - `floor`: cannot get down to the floor and up — a bed move takes its chair stand-in
 * - `noBand`, `noStairs`: what the person said they have
 * - `bpFinisher`: high BP, under control — SB gains the wall-sit finisher
 * - `balanceGoal`: the balance block before the walk on walk days
 * - `joint:<key>`: a joint that limits them — a cue on every move that loads it
 * - `eyes`: eye treatment for diabetes — the moves flagged `bp` (head below the heart, a held
 *   breath) are swapped as for high BP, with the eyes as the reason (`trainingRules`' noHeadsDown)
 * - `pain:<key>` / `painOut:<key>`: How was it? reported that joint over 5 of 10 once / twice in 28
 *   days (`readiness.ts::painRule`) — its moves one step easier, or left out with the physio line
 */
export type FillFlag =
  | MoveFlag
  | "steady"
  | "floor"
  | "noBand"
  | "noStairs"
  | "bpFinisher"
  | "balanceGoal"
  | "eyes"
  | `joint:${JointKey}`
  | `pain:${JointKey}`
  | `painOut:${JointKey}`;

const JOINTS: readonly JointKey[] = ["neck", "shoulder", "back", "hip", "knee", "ankle", "wrist"];

/** The conditions as strings: the questionnaire's list grows (plan §3.5 adds osteoporosis,
 *  jointReplacement, heart, lung, stroke) and a key this build has not typed still counts. */
function conditions(S: KayaState): readonly string[] {
  return (S.profile.eating?.conditions ?? []) as readonly string[];
}

function hasCond(S: KayaState, c: string): boolean {
  return conditions(S).includes(c);
}

/** "BP under control?" — asked inline under High BP; read from either block, unknown is no. */
export function bpControlled(S: KayaState): boolean {
  // The questionnaire stores "yes" | "no" | "unsure" (ADR-053); P2 read a boolean, so the father's
  // "Yes" never reached the wall-sit finisher (P9). Both are read; true is an older blob's.
  const yes = (v: unknown) => v === "yes" || v === true;
  const e = S.profile.eating as { bpControlled?: unknown } | undefined;
  return yes(S.profile.training?.bpControlled) || yes(e?.bpControlled);
}

function ageOn(S: KayaState, ds: string): number | null {
  const y = S.profile.birthYear;
  const year = parseDay(ds).getFullYear();
  if (!y || y < 1900 || y > year - 5) return null;
  return year - y;
}

/** The person's flags, sorted, so equal answers give an equal list (it is frozen in `DayRan`). */
export function trainingFlags(S: KayaState, device?: TrainingDevice, ds = todayStr()): FillFlag[] {
  const T = S.profile.training;
  const E = S.profile.eating;
  const out = new Set<FillFlag>();

  for (const j of JOINTS) if (T?.joints?.[j]) out.add(`joint:${j}`);
  if (T?.joints?.knee) out.add("knee");
  if (T?.joints?.hip && hasCond(S, "jointReplacement")) out.add("hipReplacement");
  if (hasCond(S, "osteoporosis")) out.add("osteoporosis");
  if (hasCond(S, "bp")) out.add("bp");
  if ((E?.meds ?? []).includes("bp")) out.add("dizzy");
  if (T?.falls === "once" || T?.falls === "more" || T?.unsteady === "sometimes" || T?.unsteady === "often") {
    out.add("balance");
  }
  if (E?.pregnant === "pregnant") out.add("pregnant");
  if (device?.recent || device?.doctor) out.add("recent");
  // a sore on the foot is answered under diabetes on Still right? (ADR-053): chair and bed only
  if (device?.footSore || T?.feet === "sore") out.add("footSore");
  if (T?.eyesTreated) out.add("eyes");
  for (const [j, r] of Object.entries(painRule(S, ds)) as [JointKey, JointRule][]) {
    out.add(r.level === "out" ? `painOut:${j}` : `pain:${j}`);
  }

  if (steadyLevel(S, ds) === "steady") out.add("steady");
  if (T?.floor === "no") out.add("floor");
  if (Array.isArray(T?.have)) {
    if (!T.have.includes("band")) out.add("noBand");
    if (!T.have.includes("stairs")) out.add("noStairs");
  }
  // the finisher is the BP goal's, and only once the BP is under control (report §3.1, §4)
  if (hasCond(S, "bp") && bpControlled(S) && T?.goals?.includes("bp")) out.add("bpFinisher");
  if (T?.goals?.includes("balance")) out.add("balanceGoal");
  return [...out].sort();
}

/* ---------- the policy, one table per decision ---------- */

/** A move flag that swaps the move for its `easier` id, or drops it: "not as written". */
const SWAP_FLAGS: readonly MoveFlag[] = ["knee", "hipReplacement", "osteoporosis", "bp", "recent", "footSore", "pregnant"];
/** A person's flag that swaps by another's move flag: treated eyes avoid what high BP avoids. */
const SWAP_AS: readonly (readonly [FillFlag, MoveFlag])[] = [...SWAP_FLAGS.map((f) => [f, f] as const), ["eyes", "bp"]];

/**
 * Knee-flagged moves that are the treatment for a painful knee rather than a risk to it — leg
 * strength stays in the plan (NICE NG226, OARSI 2019; report §3.1 "Knee OA") — kept with the
 * softer way to do them. Everything else the knee flag marks (deep or loaded bending, kneeling)
 * is swapped.
 */
const KNEE_KEEP: Readonly<Record<ExerciseId, string>> = {
  sitstand: "with hands, from a firm high seat",
  kneeext: "a small, pain-free range",
  wallsit: "shallow",
  minisquat: "shallow",
};

/** The chair or wall version of a bed move, for someone who cannot get down and up (report §3.1). */
const CHAIR_FOR: Readonly<Record<ExerciseId, ExerciseId>> = {
  quadset: "kneeext",
  slr: "kneeext",
  bridge: "sitstand",
  clam: "sideleg",
  deadbug: "standbirddog",
  curlup: "wallplank",
  sphinx: "backext",
  heelslide: "seatmarch",
};

/** The support rung as words: a cue, never a different move (plan §3.2). A stand holds on; a
 *  walk along the counter can only trail a hand, so its rungs are fewer. */
const RUNG_HOLD: readonly string[] = [
  "both hands on the {}",
  "one hand on the {}",
  "fingertips on the {}",
  "no hands, the {} in reach",
  "no hands; turn your head slowly, the {} in reach",
];
const RUNG_WALK: readonly string[] = [
  "a hand on the {}",
  "a hand on the {}",
  "fingertips on the {}",
  "the {} in reach",
  "the {} in reach",
];

function rungCue(rung: number, ex: Exercise, timed: boolean): string {
  const surface = ex.tags?.includes("wall") ? "wall" : "counter";
  const words = timed ? RUNG_HOLD : RUNG_WALK;
  return words[Math.max(0, Math.min(words.length - 1, rung))].replace("{}", surface);
}

/* ---------- the words ---------- */

function lower(name: string): string {
  // a name that starts with a proper noun keeps it: "a McGill curl-up"
  const first = name.split(/[\s-]/)[0];
  if (/[A-Z]/.test(first.slice(1))) return name;
  return name.charAt(0).toLowerCase() + name.slice(1);
}

/** "a heel-to-toe stand", "an overhead press" */
function aName(name: string): string {
  const n = lower(name);
  return (/^[aeiou]/.test(n) ? "an " : "a ") + n;
}

function jointWords(S: KayaState | null, j: JointKey): string {
  const side = S?.profile.training?.joints?.[j]?.side;
  if (j === "back" || j === "neck") return `your ${j}`;
  if (side === "both") return `your ${j}s`;
  if (side === "left" || side === "right") return `your ${side} ${j}`;
  return `your ${j}`;
}

/** Why a flag changed a move, in the person's words. */
function flagWords(S: KayaState | null, f: FillFlag): string {
  switch (f) {
    case "knee":
      return jointWords(S, "knee");
    case "hipReplacement":
      return "your new hip";
    case "osteoporosis":
      return "your bones";
    case "bp":
      return "your BP";
    case "recent":
      return "until your doctor says yes";
    case "footSore":
      return "your feet";
    case "pregnant":
      return "your pregnancy";
    case "steady":
      return "Steady";
    case "floor":
      return "getting down and up is hard";
    case "noBand":
      return "no band";
    case "noStairs":
      return "no stairs";
    case "eyes":
      return "your eyes";
    default:
      return "";
  }
}

/** Whether a move loads a joint: by its tag, and a back by bending or twisting. */
function loads(ex: Exercise, j: JointKey): boolean {
  const tag = JOINT_TAG[j];
  if (tag) return !!ex.tags?.includes(tag);
  return j === "back" && (!!ex.tags?.includes("flexion") || !!ex.tags?.includes("rotation"));
}

/* ---------- filling a template ---------- */

export interface FilledSession extends Session {
  /** why a move differs from the template's, keyed by the move that runs */
  reasons: Record<ExerciseId, string>;
  /** slots left out, with why */
  dropped: { id: ExerciseId; note: string }[];
  /** the line under the session's name: BP tablets, a held breath */
  lead: string | null;
}

export interface FillOptions {
  device?: TrainingDevice;
  /** the flags a frozen day was filled with (`DayRan.flags`); they win over the person's today */
  flags?: readonly string[];
  /** the short version (`shortVersion`) */
  short?: boolean;
  ds?: string;
}

const SEATED = new Set(["chair", "bed"]);

function seated(ex: Exercise | undefined): boolean {
  const pos = ex?.tags?.find((t) => ["chair", "standing", "support", "wall", "bed"].includes(t));
  return pos ? SEATED.has(pos) : false;
}

interface Choice {
  ex: Exercise;
  note: string | null;
}

/** The joints a move's tags can name; a back is read off flexion and rotation instead. */
const JOINT_TAG: Partial<Record<JointKey, MoveTag>> = {
  knee: "knee",
  hip: "hip",
  shoulder: "shoulder",
  wrist: "wrist",
  neck: "neck",
};

/** One slot through the flags: the move that runs and why, or null when nothing clean is left. */
function pickFor(S: KayaState | null, slot: Slot, flags: ReadonlySet<string>): Choice | { drop: string } {
  const first = moveById(slot.move);
  if (!first) return { drop: "not in this build" };
  let ex: Exercise = first;
  let note: string | null = null;
  const because = (why: string) => {
    if (!note) note = `instead of ${aName(first.n)}: ${why}`;
  };

  if (flags.has("steady") && slot.steady) {
    const st = moveById(slot.steady);
    if (st) {
      ex = st;
      because(flagWords(S, "steady"));
    }
  }

  if (flags.has("floor") && ex.tags?.includes("bed") && !ex.tags.includes("breath")) {
    const to = CHAIR_FOR[ex.id] ? moveById(CHAIR_FOR[ex.id]) : undefined;
    if (!to) return { drop: `${first.n} left out: ${flagWords(S, "floor")}` };
    ex = to;
    because(flagWords(S, "floor"));
  }

  if (flags.has("noStairs") && ex.id === "stepup") {
    const to = moveById("sitstand");
    if (to) {
      ex = to;
      because(flagWords(S, "noStairs"));
    }
  }

  // Down the easier chain while a swapping flag still marks the move: at most three steps.
  for (let step = 0; ; step++) {
    const hit = SWAP_AS.find(
      ([f, m]) => flags.has(f) && (ex.flags ?? []).includes(m) && !(m === "knee" && KNEE_KEEP[ex.id] !== undefined)
    )?.[0];
    if (!hit) break;
    const to = step < 3 && ex.easier ? moveById(ex.easier) : undefined;
    if (!to) return { drop: `${first.n} left out: ${flagWords(S, hit)}` };
    ex = to;
    because(flagWords(S, hit));
  }

  // How was it? (ADR-054, painRule): a joint that hurt twice is rested with the physio line; once,
  // its moves go one step easier, and out when there is no easier one.
  const sore = JOINTS.find((j) => (flags.has(`pain:${j}`) || flags.has(`painOut:${j}`)) && loads(ex, j));
  if (sore) {
    const hurt = `${jointWords(S, sore)} hurt`;
    if (flags.has(`painOut:${sore}`)) return { drop: `${first.n} left out: ${hurt} after two sessions — see a physiotherapist` };
    const to = ex.easier ? moveById(ex.easier) : undefined;
    if (!to) return { drop: `${first.n} left out: ${hurt} last time` };
    ex = to;
    note = null;
    because(`${hurt} last time`);
  }

  if (!note && flags.has("knee") && KNEE_KEEP[ex.id]) note = `${KNEE_KEEP[ex.id]}: ${flagWords(S, "knee")}`;
  // A joint's cue goes on the strength moves that load it, not on every step or march.
  if (!note && (ex.t === "legs" || ex.t === "upper" || ex.t === "core") && !ex.tags?.includes("isometric")) {
    const j = JOINTS.find((k) => {
      const tag = JOINT_TAG[k];
      return flags.has(`joint:${k}`) && !!tag && !!ex.tags?.includes(tag);
    });
    if (j === "knee") note = `a small bend: ${jointWords(S, "knee")}`;
    else if (j) note = `only as far as is comfortable: ${jointWords(S, j)}`;
    else if (flags.has("joint:back") && (ex.tags?.includes("flexion") || ex.tags?.includes("rotation"))) {
      note = `only as far as is comfortable: ${jointWords(S, "back")}`;
    }
  }
  if (!note && flags.has("noBand") && ex.id === "row") note = `${flagWords(S, "noBand")}: pull a towel apart hard`;
  return { ex, note };
}

function doseFor(slot: Slot, ex: Exercise, swapped: boolean): Pick<BlockItem, "r" | "t"> {
  // The template's dose was written for its own move; a swapped-in move runs its own.
  if (!swapped && (slot.r || slot.t)) return slot.r ? { r: slot.r } : { t: slot.t };
  if (ex.dose?.r) return { r: ex.dose.r };
  if (ex.dose?.t) return { t: ex.dose.t };
  return slot.r ? { r: slot.r } : slot.t ? { t: slot.t } : { r: [8, 10, 12] };
}

function fillBlock(
  S: KayaState | null,
  b: TemplateBlock,
  flags: ReadonlySet<string>,
  rung: number,
  seen: Set<ExerciseId>,
  out: FilledSession
): Block {
  const items: BlockItem[] = [];
  for (const slot of b.items) {
    const p = pickFor(S, slot, flags);
    if ("drop" in p) {
      out.dropped.push({ id: slot.move, note: p.drop });
      continue;
    }
    const { ex } = p;
    let { note } = p;
    // a swap that lands on a move the block already has would only repeat it
    if (ex.id !== slot.move && seen.has(ex.id)) {
      const n = note ? note.replace(/^instead of [^:]*: /, "") : "";
      out.dropped.push({ id: slot.move, note: `${moveById(slot.move)?.n ?? slot.move} left out${n ? ": " + n : ""}` });
      continue;
    }
    seen.add(ex.id);
    const cues: string[] = [];
    if (slot.cue && ex.id === slot.move) cues.push(slot.cue);
    const dose = doseFor(slot, ex, ex.id !== slot.move);
    if (ex.t === "balance") cues.push(rungCue(rung, ex, !!dose.t));
    const it: BlockItem = { id: ex.id, ...dose };
    const side = ex.dose?.side ?? slot.move === "kneechest";
    if (side) it.side = true;
    if (ex.hipfree) it.hipfree = true;
    if (cues.length) it.cue = cues.join("; ");
    if (note) {
      it.note = note;
      out.reasons[ex.id] = note;
    }
    items.push(it);
  }
  const { items: _slots, ...rest } = b;
  void _slots;
  return { ...rest, items };
}

/**
 * Marks the "Stand up slowly" rests: a standing move whose move before it was done sitting. Worked
 * out after the fill, because a swap can turn a standing slot into a chair move and back.
 */
function markStandUps(blocks: Block[]): void {
  let prevLast: Exercise | undefined;
  for (const b of blocks) {
    const exs = b.items.map((it) => moveById(it.id));
    b.items.forEach((it, i) => {
      if (i === 0 || seated(exs[i])) return;
      if (seated(exs[i - 1])) it.standUp = true;
    });
    const first = exs[0];
    const last = exs[exs.length - 1];
    if (first && !seated(first)) {
      // round two starts after the block's own last move; the block after the previous block's
      if (b.items.length > 1 && seated(last)) b.items[0].standUp = true;
      if (prevLast && seated(prevLast)) b.standUp = true;
    }
    if (last) prevLast = last;
  }
}


/**
 * The template for `key`, filled for this person (ADR-052). `S` null fills it for nobody — the
 * template as written, which is what a page with no state, or the server, reads.
 */
export function fillTemplate(S: KayaState | null, key: SteadyKey, opts: FillOptions = {}): FilledSession {
  return fillFrom(S, TEMPLATES[key], opts);
}

/** `fillTemplate` for any template: the tests fill slots no shipped template has. */
export function fillFrom(S: KayaState | null, t: Template, opts: FillOptions = {}): FilledSession {
  const key = t.key;
  const out: FilledSession = { name: t.name, type: key, mins: t.mins[0], how: t.how, blocks: [], reasons: {}, dropped: [], lead: null };
  const flags: ReadonlySet<string> = new Set(
    S ? (opts.flags ?? trainingFlags(S, opts.device, opts.ds)) : opts.flags ?? []
  );
  const rung = S?.profile.training?.supportRung ?? 0;

  let blocks: TemplateBlock[] = [...t.blocks];
  if (key === "SB" && flags.has("bpFinisher")) blocks.splice(blocks.length - 1, 0, BP_FINISHER);
  if (key === "WK" && flags.has("balanceGoal")) blocks.splice(1, 0, SB_BALANCE);
  if (opts.short) blocks = shortBlocks(key, blocks);

  const seen = new Set<ExerciseId>();
  out.blocks = blocks
    .map((b) => {
      seen.clear();
      return fillBlock(S, b, flags, rung, seen, out);
    })
    .filter((b) => b.items.length > 0);
  markStandUps(out.blocks);

  if (flags.has("dizzy")) out.lead = "Count out loud on every hold, and stand up slowly — you take BP tablets.";
  else if (flags.has("bp")) out.lead = "Count out loud on every hold; never hold the breath.";
  if (opts.short) out.name = `${t.name} · short version`;
  return out;
}

/* ---------- the short version ---------- */

/**
 * A bad day's version of a template (plan §3.4): one round, the warm-up, the first half of each
 * main block, the cool-down — 10 to 15 minutes. It counts as done and never steps anyone up; the
 * day's frozen block says it ran short (`DayRan.short`). The walk keeps its first ten minutes.
 */
function shortBlocks(key: SteadyKey, blocks: TemplateBlock[]): TemplateBlock[] {
  if (blocks.length <= 2) return blocks;
  return blocks.map((b, i) => {
    if (i === 0 || i === blocks.length - 1) return b;
    const half = b.items.slice(0, Math.max(1, Math.floor(b.items.length / 2))).map((s) =>
      key === "WK" && s.pattern === "walk" ? { ...s, t: [600, 600, 600] as [number, number, number] } : s
    );
    return { ...b, rounds: [1, 1, 1] as [number, number, number], items: half };
  });
}

/** The short version of a Steady template, or null for a Kaya session (it has the rescue). */
export function shortVersion(S: KayaState, key: SessionKey, opts: FillOptions = {}): FilledSession | null {
  return isSteadyKey(key) ? fillTemplate(S, key, { ...opts, short: true }) : null;
}

/* ---------- the programme and the level ---------- */

/**
 * Which programme the person follows: the one they chose, or Steady for someone 60 or over who
 * has answered the exercise questions and not chosen. Never Steady without answers — a profile
 * without `training` keeps the Kaya week exactly as it was.
 */
export function programmeFor(S: KayaState, ds = todayStr()): Programme {
  const T = S.profile.training;
  if (!T || typeof T.at !== "number") return "kaya";
  if (T.programme) return T.programme;
  const a = ageOn(S, ds);
  return a !== null && a >= 60 ? "steady" : "kaya";
}

/**
 * The Steady programme's level (plan §3.4): "steady" for a failed test, a fall in the last year or
 * "unsteady"; "build" above it. A fall or unsteadiness answered after the tests still places
 * Steady. The latest tests decide through `fitnessTests.ts::placement` — the one rule, the same one
 * the tests' Save wrote into `level` — so counts a linked owner typed in (ADR-056) place the person
 * too; without tests, the level the tests last wrote; without that, 60 and over starts at Steady
 * (plans-by-age-sex-goal.md §4.4).
 */
export function steadyLevel(S: KayaState, ds = todayStr()): "steady" | "build" {
  const T = S.profile.training;
  if (T?.falls === "once" || T?.falls === "more" || T?.unsteady === "sometimes" || T?.unsteady === "often") {
    return "steady";
  }
  const lt = latestTests(S, ds);
  const placed = lt ? placement(S, lt.tests, parseDay(ds), T ?? {}) : null;
  if (placed) return placed.level;
  if (T?.level === "steady" || T?.level === "build") return T.level;
  const a = ageOn(S, ds);
  return a !== null && a >= 60 ? "steady" : "build";
}

/** The chair stand's STEADI line at an age, by the person's sex (fitnessTests' table). */
function chairLine(S: KayaState, a: number): number | null {
  return rangeFor("chair", a, S.profile.sex)?.line ?? null;
}

/* ---------- the week ---------- */

const STRENGTH_GOALS: readonly TrainingGoal[] = ["strength", "balance", "joints"];
const STRUCTURED: readonly SessionKey[] = ["SB", "GC", "CH", "WK"];

function dayCap(days: TrainingProfile["days"]): number {
  return days === "2-3" ? 3 : days === "4-5" ? 5 : 6;
}

export interface SteadyWeekOptions {
  age?: number | null;
  /** today, for the first fortnight of a person who was not active */
  today?: string;
}

/**
 * The Steady week, Sunday first (report §4; plan §3.3; plans-by-age-sex-goal.md §2.4):
 * - 60–74 at Build: SB · WK · GC · WK · SB · WK, Sunday rest;
 * - at Steady: the strength days stay, at the counter and the chair, and the walk days are Morning
 *   joints with the walks Kaya records beside them;
 * - 75 and over: the strength days are the Chair session, SB's balance on Wednesday when a goal
 *   asks for three strength days;
 * - the goals: the larger frequency each asks for — three strength days for strength, balance or
 *   joints, walks on five days for weight, a walk instead of GC for "just keep moving" — and every
 *   goal's never: strength never on two days running, never two idle days with blood sugar;
 * - the answers cap it: `days` (3, 5 or 6 structured days, the rest rest) and `minutes` (10 is
 *   the Chair session and Morning joints);
 * - "hardly active" at Steady: the Chair session three times for the first two weeks, nothing
 *   else structured.
 */
export function steadyWeek(T: TrainingProfile, level: "steady" | "build", opts: SteadyWeekOptions = {}): SessionKey[] {
  const goals = T.goals ?? [];
  const old = (opts.age ?? 0) >= 75;
  const today = opts.today ?? todayStr();

  if (level === "steady" && T.active === "none" && typeof T.at === "number") {
    const start = todayStr(new Date(T.at));
    if (daysBetween(start, today) < 14) return ["R", "CH", "R", "CH", "R", "CH", "R"];
  }

  const three = goals.some((g) => STRENGTH_GOALS.includes(g));
  const strength: SessionKey = old ? "CH" : "SB";
  const walkDay: SessionKey = level === "steady" || old ? "MJ" : "WK";
  const mid: SessionKey = three ? "SB" : goals.includes("active") ? walkDay : old ? "CH" : "GC";

  const w: SessionKey[] = ["R", strength, walkDay, mid, walkDay, strength, walkDay];
  if (goals.includes("weight") && walkDay === "WK") w[0] = "WK";
  if (goals.includes("active")) w[0] = "MJ";

  if (T.minutes === 10) {
    for (let i = 0; i < 7; i++) {
      if (w[i] === "SB") w[i] = "CH";
      else if (w[i] === "GC" || w[i] === "WK") w[i] = "MJ";
    }
  }

  // The days answered cap the structured days; the last walk days go first, strength days last.
  const cap = dayCap(T.days);
  const order = [6, 4, 2, 0, 3, 5, 1];
  let n = w.filter((k) => STRUCTURED.includes(k)).length;
  for (const i of order) {
    if (n <= cap) break;
    if (STRUCTURED.includes(w[i])) {
      w[i] = "R";
      n--;
    }
  }

  // Blood sugar: never two idle days in a row, the week wrapping round.
  if (goals.includes("sugar")) {
    for (let i = 0; i < 7; i++) if (w[i] === "R" && w[(i + 6) % 7] === "R") w[i] = "MJ";
  }
  return w;
}

/** The Steady week for this person, when they follow the Steady programme; null otherwise. */
export function steadyWeekFor(S: KayaState, ds = todayStr()): SessionKey[] | null {
  const T = S.profile.training;
  if (!T || programmeFor(S, ds) !== "steady") return null;
  return steadyWeek(T, steadyLevel(S, ds), { age: ageOn(S, ds), today: ds });
}

/* ---------- the rule line ---------- */

function conditionReason(S: KayaState, g: TrainingGoal): string | null {
  const meds = S.profile.eating?.meds ?? [];
  if (g === "sugar") {
    if (hasCond(S, "prediabetes")) return "you have prediabetes";
    if (hasCond(S, "diabetes")) return "you have diabetes";
    return null;
  }
  if (g === "bp" && hasCond(S, "bp")) {
    if (bpControlled(S)) return meds.includes("bp") ? "high BP, under control on tablets" : "high BP, under control";
    return meds.includes("bp") ? "high BP, on tablets" : "high BP";
  }
  if (g === "joints") {
    const j = JOINTS.find((k) => S.profile.training?.joints?.[k]);
    return j ? jointWords(S, j) : null;
  }
  return null;
}

function testReason(S: KayaState, ds: string): string | null {
  const lt = latestTests(S, ds);
  const a = ageOn(S, ds);
  if (lt && a !== null) {
    const line = chairLine(S, a);
    const n = lt.tests.chairStand;
    if (typeof n === "number" && line !== null && n < line) {
      return `you stood ${n} times in 30 seconds (the usual range at ${a} starts at ${line})`;
    }
    const st = lt.tests.balanceStage;
    if (typeof st === "number" && st < 3) {
      const s = lt.tests.balanceSecs;
      return typeof s === "number"
        ? `you held the heel-to-toe stand for ${s} seconds (10 is the line)`
        : "the heel-to-toe stand was not yet 10 seconds";
    }
    return null;
  }
  const T = S.profile.training;
  if (T?.falls === "once" || T?.falls === "more") return "a fall in the last year";
  return a !== null && a >= 60 ? `you are ${a}` : null;
}

function join(parts: string[], last: string): string {
  if (parts.length <= 1) return parts.join("");
  if (parts.length === 2) return `${parts[0]}${last}${parts[1]}`;
  return parts.join(", ");
}

/**
 * The plan's one line (plan §3.3, plans-by-age-sex-goal.md §5.1): what the week asks and why, the
 * rule and the evidence together — the person's figure beside the published range, never a grade.
 * Null for the Kaya programme, whose Train page is unchanged.
 */
export function ruleLine(S: KayaState, ds = todayStr()): string | null {
  const T = S.profile.training;
  if (!T || programmeFor(S, ds) !== "steady") return null;
  const goals = T.goals ?? [];
  const week = steadyWeekFor(S, ds) ?? [];
  const strengthDays = week.filter((k) => k === "SB" || k === "CH").length;
  const meals = (T.mealWalks?.length ?? 0) > 0 || hasCond(S, "diabetes") || hasCond(S, "prediabetes");
  const finisher = hasCond(S, "bp") && bpControlled(S) && week.includes("SB");
  const num = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven"][strengthDays] ?? String(strengthDays);

  const clauses: string[] = [];
  const reasons: string[] = [];
  const add = (c: string) => {
    if (!clauses.includes(c)) clauses.push(c);
  };
  const because = (r: string | null) => {
    if (r && !reasons.includes(r)) reasons.push(r);
  };

  for (const g of goals) {
    if (g === "balance") {
      add(`${num} strength-and-balance days`);
      because(testReason(S, ds));
    } else if (g === "strength") {
      add(`strength ${num.toLowerCase()} days`);
      because(testReason(S, ds));
    } else if (g === "joints") {
      add(`${num} strength days for the joints`);
      because(conditionReason(S, g));
    } else if (g === "sugar") {
      add(meals ? "walks after meals" : "a walk most days");
      because(conditionReason(S, g));
    } else if (g === "bp") {
      because(conditionReason(S, g));
    } else if (g === "weight") {
      add("walks on five days");
    } else if (g === "active") {
      add("a short session most days");
    }
  }
  // BP's first ask is walking on most days, and its finisher closes the line (report §4).
  if (goals.includes("bp")) {
    clauses.unshift("walking most days");
    if (finisher) clauses.push("a wall sit");
  }
  if (!clauses.length) add(`${num} strength-and-balance days`);

  const head = join(clauses, " and ");
  const line = head.charAt(0).toUpperCase() + head.slice(1);
  return reasons.length ? `${line}: ${join(reasons, ", and ")}.` : `${line}.`;
}

/* ---------- Today: the walk after a meal, and the heat ---------- */

export const BREAKFAST_AT_DEFAULT = "09:00";
export const LUNCH_AT_DEFAULT = "13:00";
/** A meal's walk is offered for 90 minutes after it, and a walk begun within two hours counts. */
const DUE_MIN = 90;
const COUNTS_MIN = 120;

export interface MealWalk {
  meal: "breakfast" | "lunch" | "dinner";
  title: string;
  /** "HH:MM" the meal was eaten, or is expected */
  at: string;
  state: "later" | "due" | "done" | "past";
}

function hhmm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function atOn(ds: string, hm: string): number {
  const [h, m] = hm.split(":").map(Number);
  const d = parseDay(ds);
  d.setHours(h, m, 0, 0);
  return d.getTime();
}

/**
 * The after-meal walks for a Steady person who asked for them or has diabetes or prediabetes
 * (plan §3.3, "The Indian rhythm"): ten minutes after lunch and after dinner, timed off the meal
 * when it is logged and off the dinner reminder's hour when it is not. Done when a walk began
 * within two hours of the meal. An empty list for everyone else.
 */
export function mealWalkRow(S: KayaState, ds = todayStr(), now = Date.now(), anyProgramme = false): MealWalk[] {
  const T = S.profile.training;
  const steady = !!T && programmeFor(S, ds) === "steady";
  // `anyProgramme`: the walks someone on the Kaya programme picked under When — the reminder
  // scheduler reads them (trainingNudge.ts); Today's row stays the Steady programme's
  if (!T || (!steady && !(anyProgramme && T.mealWalks?.length))) return [];
  const sugar = hasCond(S, "diabetes") || hasCond(S, "prediabetes");
  const meals = T.mealWalks?.length ? T.mealWalks : steady && sugar ? (["lunch", "dinner"] as const) : [];
  const L = S.logs[ds];
  const starts = [
    ...(L?.walks ?? []).map((w) => new Date(w.startedAt).getTime()),
    ...(L?.treadmill?.startedAt ? [new Date(L.treadmill.startedAt).getTime()] : []),
  ];
  return meals.map((meal) => {
    const eaten = (L?.food ?? []).filter((e) => e.meal === meal).map((e) => e.at);
    const fallback = meal === "breakfast" ? BREAKFAST_AT_DEFAULT : meal === "lunch" ? LUNCH_AT_DEFAULT : dinnerSettings(S).at;
    const at = eaten.length ? Math.min(...eaten) : atOn(ds, fallback);
    const done = starts.some((t) => t >= at - 15 * 60000 && t <= at + COUNTS_MIN * 60000);
    const state: MealWalk["state"] = done ? "done" : now < at ? "later" : now <= at + DUE_MIN * 60000 ? "due" : "past";
    return { meal, title: `Walk · 10 minutes after ${meal}`, at: hhmm(at), state };
  });
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
/** Sunset's stand-in: an offline app cannot know it, and in an Indian summer it is near half past six. */
export const SUNSET_HOUR = 18.5;

export interface HeatWindow {
  month: string;
  /** the muted line on Today and the walk screen */
  line: string;
  /** a walk outdoors suits this hour: before 8 or after sunset */
  outdoors: boolean;
  /** noon to 3 pm: nothing outdoors (NDMA) */
  noon: boolean;
}

/**
 * From April to June a walk belongs before 8 am or after sunset, and nothing outdoors from noon to
 * 3 pm (NDMA's heat-wave advice; older-adults-guidelines.md §8 gives 9 am, the plan 8). The month
 * and the hour only — no weather. Null the rest of the year.
 */
export function heatWindow(ds: string, hour: number): HeatWindow | null {
  const m = parseDay(ds).getMonth();
  if (m < 3 || m > 5) return null;
  const month = MONTHS[m];
  return {
    month,
    line: `It’s ${month}: walk before 8 or after sunset`,
    outdoors: hour < 8 || hour >= SUNSET_HOUR,
    noon: hour >= 12 && hour < 15,
  };
}

/* ---------- the week as rows ---------- */

/** The words a Steady week row shows beside a Morning joints day with walks: "Joints + 2 walks". */
export function walkDayName(S: KayaState, key: SessionKey): string | null {
  const T = S.profile.training;
  if (key !== "MJ" || !T?.mealWalks?.length) return null;
  const n = T.mealWalks.length;
  return `Joints + ${n} walk${n === 1 ? "" : "s"}`;
}

/** "after lunch and dinner" */
export function mealWalkWords(S: KayaState): string | null {
  const m = S.profile.training?.mealWalks ?? [];
  return m.length ? `after ${m.join(" and ")}` : null;
}

/** Strength days in a week, SB and CH: the Steady programme's own count (no streak, no badge). */
export function strengthDaysIn(week: readonly SessionKey[]): number {
  return week.filter((k) => k === "SB" || k === "CH").length;
}

/** The first day of the person's programme week, for "week n": the day the answers were saved. */
export function programmeWeekNo(S: KayaState, ds = todayStr()): number {
  const at = S.profile.training?.at;
  if (typeof at !== "number") return 1;
  const start = todayStr(new Date(at));
  const mon = addDays(start, -((parseDay(start).getDay() + 6) % 7));
  return Math.max(1, Math.floor(daysBetween(mon, ds) / 7) + 1);
}
